import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateInWords } from 'utils/formatDate';
import {
  analizarAbandono,
  generarEscritoAbandono,
  recalcularAbandono,
} from 'services/abandonoService';

const TRI_ESTADOS = ['si', 'no', 'indeterminado'];

/**
 * La hipótesis es una decisión jurídica y el abogado tiene que poder fijarla, igual que las
 * compuertas: seis meses de inactividad en el cuaderno principal contra tres años en el apremio.
 *
 * Sin este selector, una corrida que el modelo deja en indeterminada no puede generar escrito —el
 * cómputo no sabe si son 6 meses o 36— y el rechazo manda a corregir algo que no estaba en la
 * pantalla. Pasó en la primera corrida real, el 07-09-2026.
 */
const HIPOTESIS = [
  { value: 'AB6', label: 'AB6 — seis meses sin gestión útil' },
  { value: 'AB3', label: 'AB3 — tres años, gestiones en el apremio' },
  { value: 'RENOVACION', label: 'Renovación — hubo notificación que renovó el procedimiento' },
  { value: 'NO_PROCEDE', label: 'No procede' },
  { value: 'INDETERMINADA', label: 'Indeterminada' },
];

const ESTADOS_ADMINISTRATIVOS = [
  { value: 'sin_archivar', label: 'Sin archivar' },
  { value: 'archivada', label: 'Archivada — el escrito lleva otrosí de desarchivo' },
  { value: 'indeterminado', label: 'Indeterminado' },
];

const FUENTES = [
  { value: 'actuacion_receptor', label: 'Consta el atestado del receptor' },
  { value: 'manual_abogado', label: 'La verifiqué yo en el OJV' },
  { value: 'indicio_embargo', label: 'No consta la fecha, pero hubo embargo' },
  {
    value: 'resolucion_tribunal',
    label: 'Se dedujo de una resolución (no prueba)',
  },
  { value: 'no_consta', label: 'No consta' },
];

/**
 * Abandono del procedimiento (arts. 152-157 CPC).
 *
 * El producto no es una respuesta sino un análisis EDITABLE. El expediente digital habitualmente
 * no trae el atestado del receptor, así que el abogado corrige las compuertas y las fechas, y la
 * conclusión se recalcula en el backend —que es la única fuente de verdad del plazo.
 */
const AbandonoPanel = ({ DesignSystemComponents, caseData, t }) => {
  const { Button, Badge, Loader, Alert, Checkbox, Input } = DesignSystemComponents || {};

  const [ebook, setEbook] = useState([]);
  const [receptor, setReceptor] = useState([]);
  const [analisis, setAnalisis] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [error, setError] = useState(null);

  // Lo que el expediente de la contraria no dice y decide qué plantilla se usa.
  const [conMandatoJudicial, setConMandatoJudicial] = useState(false);
  const [documentoPersoneria, setDocumentoPersoneria] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [verificado, setVerificado] = useState(false);
  const [comparecientes, setComparecientes] = useState([]);
  const [escrito, setEscrito] = useState(null);
  const [generando, setGenerando] = useState(false);

  // Las plantillas solo usan la personería dentro de los bloques de persona jurídica: con un
  // ejecutado natural el párrafo se borra y el dato se descarta.
  const pideJustificarPersoneria = useMemo(
    () => (analisis?.sujetos || []).some((s) => s?.tipoPersona === 'juridica'),
    [analisis],
  );

  const procedeElAbandono = meta?.veredicto?.procede === 'si';

  // El modelo dijo que no y el recálculo dice que sí: el abogado responde por los antecedentes
  // que no constan en el ebook.
  const leDioVueltaAlModelo =
    procedeElAbandono && analisis?.conclusion?.procede === 'no';

  const abortAnalisis = useRef(null);
  const abortRecalculo = useRef(null);
  const abortGenerar = useRef(null);

  useEffect(
    () => () => {
      abortAnalisis.current?.abort();
      abortRecalculo.current?.abort();
      abortGenerar.current?.abort();
    },
    []
  );

  const analizar = useCallback(async () => {
    if (!ebook.length) return;
    abortAnalisis.current?.abort();
    const controller = new AbortController();
    abortAnalisis.current = controller;

    setCargando(true);
    setError(null);
    try {
      const data = await analizarAbandono(
        ebook,
        receptor,
        caseData?.id,
        controller.signal
      );
      // analisisBase es la vista editable: viene saneada, sin el cómputo final aplicado.
      setAnalisis(data.analisisBase);
      setComparecientes((data.analisisBase?.sujetos || []).map((s) => s.id));
      setMeta({
        veredicto: data.analisis?.conclusion,
        advertencias: data.analisis?.advertencias || [],
        datosFaltantes: data.analisis?.datosFaltantes || [],
        cascaras: data.foliosCascaraDetectados,
        huecos: data.cuadernosConFoliosFaltantes,
      });
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setCargando(false);
    }
  }, [ebook, receptor, caseData?.id]);

  const recalcular = useCallback(async (siguiente) => {
    abortRecalculo.current?.abort();
    const controller = new AbortController();
    abortRecalculo.current = controller;

    setRecalculando(true);
    try {
      const data = await recalcularAbandono(siguiente, controller.signal);
      setMeta((previo) => ({
        ...previo,
        veredicto: data.conclusion,
        advertencias: data.advertencias || [],
        datosFaltantes: data.datosFaltantes || [],
      }));
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.message);
    } finally {
      setRecalculando(false);
    }
  }, []);

  // Al corregir un antecedente, el veredicto se recalcula. Escribir la fecha de notificación
  // marca la fuente como manual_abogado: es lo único que permite afirmarla.
  const corregir = useCallback(
    (camino, valor) => {
      setAnalisis((previo) => {
        if (!previo) return previo;
        const siguiente = structuredClone(previo);
        if (camino === 'demandaNotificada')
          siguiente.causa.demandaNotificada = valor;
        if (camino === 'estadoHabilitante')
          siguiente.conclusion.estadoHabilitante = valor;
        if (camino === 'excepcionesOpuestas')
          siguiente.causa.excepcionesOpuestas = valor;
        if (camino === 'fuenteNotificacion')
          siguiente.causa.fuenteNotificacion = valor;
        if (camino === 'fechaNotificacion') {
          siguiente.causa.fechaNotificacion = valor || null;
          if (valor) siguiente.causa.fuenteNotificacion = 'manual_abogado';
        }
        if (camino === 'fechaUltimaGestionUtil') {
          siguiente.conclusion.fechaUltimaGestionUtil = valor || null;
        }
        if (camino === 'fechaRenovacion') {
          siguiente.conclusion.fechaRenovacion = valor || null;
        }
        if (camino === 'estadoAdministrativo')
          siguiente.causa.estadoAdministrativo = valor;
        if (camino === 'hipotesis') {
          siguiente.conclusion.hipotesis = valor;
          // La renovación es un campo aparte del que dependen la plantilla y el plazo fatal de
          // los cinco días: se mantiene en línea con la hipótesis elegida.
          siguiente.conclusion.renovacion = valor === 'RENOVACION';
        }
        recalcular(siguiente);
        return siguiente;
      });
    },
    [recalcular]
  );

  // El relato y el fundamento no entran en el cómputo: van al redactor del numeral 1.
  const redactar = useCallback((campo, valor) => {
    setAnalisis((previo) => {
      if (!previo) return previo;
      const siguiente = structuredClone(previo);
      siguiente.conclusion[campo] = valor;
      return siguiente;
    });
  }, []);

  const editarSujeto = useCallback((id, campo, valor) => {
    setAnalisis((previo) => {
      if (!previo) return previo;
      const siguiente = structuredClone(previo);
      const sujeto = (siguiente.sujetos || []).find((s) => s.id === id);
      if (sujeto) sujeto[campo] = valor || null;
      return siguiente;
    });
  }, []);

  const descargar = useCallback((documento) => {
    if (!documento?.archivoBase64) return;
    // atob devuelve una cadena de bytes: hay que pasarla a Uint8Array o el .docx llega corrupto.
    const bytes = Uint8Array.from(atob(documento.archivoBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = documento.nombreDeArchivo || 'abandono.docx';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  }, []);

  const generar = useCallback(async () => {
    if (!analisis || !meta?.veredicto) return;
    abortGenerar.current?.abort();
    const controller = new AbortController();
    abortGenerar.current = controller;

    setGenerando(true);
    setError(null);
    try {
      // La conclusión recalculada manda sobre la del análisis base: de ahí salen las fechas y la
      // hipótesis con que se elige la plantilla. Los sujetos van filtrados: el escrito solo nombra
      // a quienes el abogado dejó marcados.
      const data = await generarEscritoAbandono(
        {
          ...analisis,
          conclusion: { ...analisis.conclusion, ...meta.veredicto },
          sujetos: (analisis.sujetos || []).filter((s) =>
            comparecientes.includes(s.id)
          ),
        },
        conMandatoJudicial,
        documentoPersoneria.trim() || null,
        caseData?.id,
        confirmado,
        controller.signal
      );
      setEscrito(data);
      descargar(data);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setGenerando(false);
    }
  }, [
    analisis,
    meta,
    conMandatoJudicial,
    documentoPersoneria,
    confirmado,
    comparecientes,
    caseData?.id,
    descargar,
  ]);

  const colorVeredicto = useMemo(() => {
    const procede = meta?.veredicto?.procede;
    if (procede === 'si') return 'success';
    if (procede === 'no') return 'danger';
    return 'warning';
  }, [meta]);

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <div>
          <div className='fw-semibold'>{t('cw_step1')}</div>
          <input
            type='file'
            accept='application/pdf'
            multiple
            className='form-control mt-2'
            onChange={(e) => setEbook(Array.from(e.target.files || []))}
          />
        </div>

        <div>
          <div className='fw-semibold'>{t('cw_step2')}</div>
          <div className='color-gray-600 small'>{t('cw_step2_hint')}</div>
          <input
            type='file'
            accept='application/pdf'
            multiple
            className='form-control mt-2'
            onChange={(e) => setReceptor(Array.from(e.target.files || []))}
          />
        </div>

        <div>
          <Button
            label={cargando ? t('cw_analyzing') : t('cw_analyze')}
            size='sm'
            variant='primary'
            onClick={analizar}
            disabled={!ebook.length || cargando}
          />
        </div>

        {error && <Alert className='alerta-en-linea' variant='danger'>{error}</Alert>}
      </div>

      {cargando && <Loader />}

      {meta && analisis && (
        <>
          <div>
            <div className='d-flex align-items-center gap-2 mb-3'>
              {/* El Badge del sistema de diseño recibe `text` y `color`, no hijos ni `type`. */}
              <Badge
                text={t(`cw_verdict_${meta.veredicto?.procede || 'indeterminado'}`)}
                color={colorVeredicto}
                size='sm'
              />
              <span className='fw-semibold'>{meta.veredicto?.hipotesis}</span>
              {recalculando && (
                <span className='color-gray-600 small'>
                  {t('cw_recalculating')}
                </span>
              )}
            </div>

            <div className='row g-3'>
              <Dato
                label={t('cw_enforceable')}
                valor={meta.veredicto?.fechaCumplimiento}
                destacado
              />
              <Dato
                label={t('cw_lastUseful')}
                valor={meta.veredicto?.fechaUltimaGestionUtil}
              />
              <Dato
                label={t('cw_renewal')}
                valor={meta.veredicto?.fechaRenovacion}
              />
              <Dato
                label={t('cw_fatal')}
                valor={meta.veredicto?.vencimientoCincoDias}
                destacado
              />
            </div>

            {(meta.cascaras > 0 || meta.huecos > 0) && (
              <div className='color-gray-600 small mt-3'>
                {meta.cascaras > 0 && t('cw_shells', { count: meta.cascaras })}
                {meta.cascaras > 0 && meta.huecos > 0 && ' · '}
                {meta.huecos > 0 && t('cw_gaps', { count: meta.huecos })}
              </div>
            )}
          </div>

          <div className='d-flex flex-column gap-3'>
            <div>
              <div className='fw-semibold'>{t('cw_gates')}</div>
              <div className='color-gray-600 small'>{t('cw_gates_hint')}</div>
            </div>

            <div>
              <div className='small mb-1'>{t('cw_hypothesis')}</div>
              <select
                className='form-select'
                value={analisis.conclusion?.hipotesis || 'INDETERMINADA'}
                onChange={(e) => corregir('hipotesis', e.target.value)}
              >
                {HIPOTESIS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <div className='color-gray-600 small mt-1'>{t('cw_hypothesis_hint')}</div>
            </div>

            <Selector
              label={t('cw_notified')}
              valor={analisis.causa?.demandaNotificada}
              opciones={TRI_ESTADOS}
              onChange={(v) => corregir('demandaNotificada', v)}
            />
            <Selector
              label={t('cw_qualifying')}
              valor={analisis.conclusion?.estadoHabilitante}
              opciones={TRI_ESTADOS}
              onChange={(v) => corregir('estadoHabilitante', v)}
            />
            <Selector
              label={t('cw_exceptions')}
              valor={analisis.causa?.excepcionesOpuestas}
              opciones={TRI_ESTADOS}
              onChange={(v) => corregir('excepcionesOpuestas', v)}
            />

            <div>
              <div className='small mb-1'>{t('cw_adminState')}</div>
              <select
                className='form-select'
                value={analisis.causa?.estadoAdministrativo || 'indeterminado'}
                onChange={(e) => corregir('estadoAdministrativo', e.target.value)}
              >
                {ESTADOS_ADMINISTRATIVOS.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
              <div className='caption color-gray-600 mt-1'>
                {t('cw_adminState_hint')}
              </div>
            </div>

            <div>
              <div className='small mb-1'>{t('cw_source')}</div>
              <select
                className='form-select'
                value={analisis.causa?.fuenteNotificacion || 'no_consta'}
                onChange={(e) => corregir('fuenteNotificacion', e.target.value)}
              >
                {FUENTES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className='small mb-1'>{t('cw_notifyDate')}</div>
              <input
                type='date'
                className='form-control'
                value={analisis.causa?.fechaNotificacion || ''}
                onChange={(e) => corregir('fechaNotificacion', e.target.value)}
              />
              <div className='color-gray-600 small mt-1'>
                {t('cw_notifyDate_hint')}
              </div>
            </div>

            <div>
              <div className='small mb-1'>{t('cw_lastUsefulEdit')}</div>
              <input
                type='date'
                className='form-control'
                value={analisis.conclusion?.fechaUltimaGestionUtil || ''}
                onChange={(e) =>
                  corregir('fechaUltimaGestionUtil', e.target.value)
                }
              />
            </div>

            {analisis.conclusion?.renovacion && (
              <div>
                <div className='small mb-1'>{t('cw_renewalEdit')}</div>
                <input
                  type='date'
                  className='form-control'
                  value={analisis.conclusion?.fechaRenovacion || ''}
                  onChange={(e) => corregir('fechaRenovacion', e.target.value)}
                />
                <div className='caption color-gray-600 mt-1'>
                  {t('cw_renewalEdit_hint')}
                </div>
              </div>
            )}
          </div>

          <div className='border-top pt-3'>
            <p className='font-light mb-2'>{t('cw_narrative')}</p>
            <p className='caption color-gray-600'>{t('cw_narrative_hint')}</p>

            <div className='mb-3'>
              <div className='small mb-1'>{t('cw_usefulDescription')}</div>
              <textarea
                className='form-control'
                rows={3}
                value={analisis.conclusion?.descripcionUltimaGestionUtil || ''}
                onChange={(e) =>
                  redactar('descripcionUltimaGestionUtil', e.target.value)
                }
              />
            </div>

            <div>
              <div className='small mb-1'>{t('cw_rationale')}</div>
              <textarea
                className='form-control'
                rows={4}
                value={analisis.conclusion?.fundamento || ''}
                onChange={(e) => redactar('fundamento', e.target.value)}
              />
            </div>
          </div>

          <div className='border-top pt-3'>
            <p className='font-light mb-2'>{t('cw_parties')}</p>
            <p className='caption color-gray-600'>{t('cw_parties_hint')}</p>

            {(analisis.sujetos || []).length === 0 && (
              <Alert className='alerta-en-linea' variant='danger'>
                {t('cw_parties_none')}
              </Alert>
            )}

            {(analisis.sujetos || []).map((s) => (
              <div key={s.id} className='border rounded p-2 mb-2'>
                {Checkbox && (
                  <Checkbox
                    id={`abandono-sujeto-${s.id}`}
                    label={`${(s.calidad || '').replaceAll('_', ' ')} · ${s.tipoPersona || ''}`}
                    checked={comparecientes.includes(s.id)}
                    onChange={(e) =>
                      setComparecientes((actuales) =>
                        e.target.checked
                          ? [...actuales, s.id]
                          : actuales.filter((id) => id !== s.id)
                      )
                    }
                  />
                )}
                {Input && (
                  <div className='row g-2 mt-1'>
                    <div className='col-12 col-md-6'>
                      <Input
                        label={t('cw_parties_name')}
                        value={s.nombre || ''}
                        onChange={(e) => editarSujeto(s.id, 'nombre', e.target.value)}
                      />
                    </div>
                    <div className='col-12 col-md-3'>
                      <Input
                        label={t('cw_parties_rut')}
                        value={s.rut || ''}
                        onChange={(e) => editarSujeto(s.id, 'rut', e.target.value)}
                      />
                    </div>
                    {s.tipoPersona === 'juridica' && (
                      <div className='col-12 col-md-3'>
                        <Input
                          label={t('cw_parties_rep')}
                          value={s.representante || ''}
                          onChange={(e) =>
                            editarSujeto(s.id, 'representante', e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className='border-top pt-3'>
            <p className='font-light mb-2'>{t('cw_writ')}</p>
            <p className='caption color-gray-600'>{t('cw_writ_hint')}</p>

            <div className='row g-3 mb-3'>
              {Checkbox && (
                <div className='col-12 col-md-5'>
                  <Checkbox
                    id='abandono-mandato-judicial'
                    label={t('cw_writ_mandate')}
                    checked={conMandatoJudicial}
                    onChange={(e) => setConMandatoJudicial(e.target.checked)}
                  />
                </div>
              )}
              {Input && pideJustificarPersoneria && (
                <div className='col-12 col-md-7'>
                  <Input
                    label={t('cw_writ_personeria')}
                    value={documentoPersoneria}
                    onChange={(e) => setDocumentoPersoneria(e.target.value)}
                    helperText={t('cw_writ_personeria_hint')}
                  />
                </div>
              )}
            </div>

            {!procedeElAbandono && (
              <Alert className='alerta-en-linea mb-3' variant='warning'>
                {t('cw_writ_blocked')}
              </Alert>
            )}

            {Checkbox && procedeElAbandono && (
              <div className='mb-3'>
                {leDioVueltaAlModelo && (
                  <Checkbox
                    id='abandono-verificado'
                    label={t('cw_writ_verified')}
                    checked={verificado}
                    onChange={(e) => setVerificado(e.target.checked)}
                  />
                )}
                <Checkbox
                  id='abandono-confirmado'
                  label={t('cw_writ_confirm')}
                  checked={confirmado}
                  onChange={(e) => setConfirmado(e.target.checked)}
                />
              </div>
            )}

            <div className='d-flex align-items-center gap-3'>
              <Button
                label={generando ? t('cw_writ_generating') : t('cw_writ_generate')}
                size='sm'
                variant='primary'
                onClick={generar}
                disabled={
                  generando ||
                  !procedeElAbandono ||
                  !confirmado ||
                  (leDioVueltaAlModelo && !verificado)
                }
              />
              {escrito && (
                <>
                  <Button
                    label={t('cw_writ_download')}
                    size='sm'
                    variant='naked'
                    onClick={() => descargar(escrito)}
                  />
                  <span className='caption color-gray-600'>
                    {t('cw_writ_template')}: {escrito.plantillaUsada}
                    {escrito.costoUsd > 0 &&
                      ` · ${t('cw_writ_cost')}: US$${escrito.costoUsd.toFixed(4)}`}
                  </span>
                </>
              )}
            </div>

            {escrito?.advertencias?.length > 0 && (
              <div className='mt-3'>
                <div className='fw-semibold mb-2'>{t('cw_writ_review')}</div>
                {escrito.advertencias.map((a, i) => (
                  <Alert className='alerta-en-linea' key={i} variant='warning'>
                    {a}
                  </Alert>
                ))}
              </div>
            )}

            {escrito?.marcadoresSinResolver?.length > 0 && (
              <div className='mt-3'>
                <div className='fw-semibold mb-2'>{t('cw_writ_unresolved')}</div>
                <ul className='mb-0'>
                  {escrito.marcadoresSinResolver.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {analisis.actuacionesPosteriores?.length > 0 && (
            <div>
              <div className='fw-semibold mb-2'>{t('cw_reviewedActions')}</div>
              <div className='caption color-gray-600 mb-2'>
                {t('cw_reviewedActions_hint')}
              </div>
              {analisis.actuacionesPosteriores.map((a, i) => (
                <div key={i} className='border-bottom py-2'>
                  <div className='d-flex align-items-center gap-2 small'>
                    {/* Una resolución de oficio no tiene presentación de parte: la fecha viene
                        en fechaResolucion y `fecha` queda nula. */}
                    <span className='fw-semibold'>
                      {formatDateInWords(a.fecha || a.fechaResolucion) || '—'}
                    </span>
                    <span className='color-gray-600'>{a.origen}</span>
                    {Badge && (
                      <Badge
                        text={a.esUtil ? t('cw_useful') : t('cw_notUseful')}
                        variant={a.esUtil ? 'success' : 'secondary'}
                      />
                    )}
                  </div>
                  {a.descripcion && <div className='small'>{a.descripcion}</div>}
                  {a.fundamento && (
                    <div className='caption color-gray-600'>{a.fundamento}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {meta.advertencias.length > 0 && (
            <div>
              <div className='fw-semibold mb-2'>{t('cw_warnings')}</div>
              {meta.advertencias.map((a, i) => (
                <Alert className='alerta-en-linea'
                  key={i}
                  variant={
                    a.startsWith('⛔') || a.startsWith('⚠️')
                      ? 'danger'
                      : 'warning'
                  }
                >
                  {a}
                </Alert>
              ))}
            </div>
          )}

          {meta.datosFaltantes.length > 0 && (
            <div>
              <div className='fw-semibold mb-2'>{t('cw_missing')}</div>
              <ul className='mb-0'>
                {meta.datosFaltantes.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Las cuatro fechas del veredicto. El backend las manda como `yyyy-MM-dd` y así salían a pantalla:
 * el abogado leía "2026-09-09" en vez de "9 de septiembre de 2026".
 */
const Dato = ({ label, valor, destacado }) => (
  <div className='col-6 col-md-3'>
    <div className='color-gray-600 small'>{label}</div>
    <div className={destacado ? 'fw-bold fs-5' : ''}>
      {formatDateInWords(valor)}
    </div>
  </div>
);

const Selector = ({ label, valor, opciones, onChange }) => (
  <div>
    <div className='small mb-1'>{label}</div>
    <select
      className='form-select'
      value={valor || 'indeterminado'}
      onChange={(e) => onChange(e.target.value)}
    >
      {opciones.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

export default AbandonoPanel;
